import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';
import type { PriorityAttribute } from '@/types/priorityAttributeType';
import type { DraggableItem } from '@amelio/ui-library';

import {
    ANONYMOUS_MINIMUM_REFUSAL,
    feedback360SettingsSchema,
    meetsAnonymousMinimum,
} from '@/constants/review/FeedbackPrivacy';

export const SelectEvaluatorsFormFields = {
    priority1: 'priority1',
    priority2: 'priority2',
    priority3: 'priority3',
    suggestedReviewerCount: 'suggestedReviewerCount',
    feedbackPrivacy: 'feedbackPrivacy',
    commentRepliesEnabled: 'commentRepliesEnabled',
} as const;

const VALIDATION_ERROR_NO_PRIORITY: TranslationKey =
    'cycleEvaluationSelectEvaluators.validation.noPriority';
const VALIDATION_ERROR_MIN_REVIEWER_COUNT: TranslationKey =
    'cycleEvaluationSelectEvaluators.validation.minReviewerCount';

const MIN_SUGGESTED_REVIEWER_COUNT = 1;

const hasAtLeastOneCriterion = (criteriaCount: number): boolean => criteriaCount > 0;

const priorityArraySchema = z.custom<DraggableItem<PriorityAttribute>[]>((value) =>
    Array.isArray(value),
);

export const selectEvaluatorsSchema = feedback360SettingsSchema
    .extend({
        priority1: priorityArraySchema,
        priority2: priorityArraySchema,
        priority3: priorityArraySchema,
        suggestedReviewerCount: z.number().min(MIN_SUGGESTED_REVIEWER_COUNT, {
            message: VALIDATION_ERROR_MIN_REVIEWER_COUNT,
        }),
    })
    .refine(
        (data) =>
            hasAtLeastOneCriterion(
                data.priority1.length + data.priority2.length + data.priority3.length,
            ),
        {
            message: VALIDATION_ERROR_NO_PRIORITY,
            path: ['priority1'],
        },
    )
    .refine(
        (data) =>
            meetsAnonymousMinimum({
                feedbackPrivacy: data.feedbackPrivacy,
                respondentCount: data.suggestedReviewerCount,
            }),
        {
            message: ANONYMOUS_MINIMUM_REFUSAL.messageKey,
            path: ['suggestedReviewerCount'],
        },
    );

export type SelectEvaluatorsFormData = z.infer<typeof selectEvaluatorsSchema>;
